import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Marker,
  GeoJSON,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  SERVICE_AREA_CENTER,
  SERVICE_AREA_ZOOM,
} from "../../data/barangayGeodata";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import NapPulseMarker from "./NapPulseMarker";
import NapQrModal from "../modals/NapQrModal";
import NapEditModal from "../modals/NapEditModal";
import NapDetailsResultModal from "../modals/NapDetailsResultModal";
import { useConfirm } from "../../context/ConfirmContext";
import MapPanelTabs from "../dashboard/MapPanelTabs";

// Ray-casting point-in-polygon test
function pointInPolygon(lat, lng, polygon) {
  // polygon is an array of [lng, lat] coordinate rings (GeoJSON format)
  let inside = false;
  for (let ring = 0; ring < polygon.length; ring++) {
    const coords = polygon[ring];
    for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
      const xi = coords[i][1], yi = coords[i][0]; // lat, lng
      const xj = coords[j][1], yj = coords[j][0];
      const intersect = ((yi > lng) !== (yj > lng)) &&
        (lat < (xj - xi) * (lng - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
  }
  return inside;
}

function pointInFeature(lat, lng, feature) {
  if (!feature || !feature.geometry) return false;
  const { type, coordinates } = feature.geometry;
  if (type === "Polygon") {
    return pointInPolygon(lat, lng, coordinates);
  } else if (type === "MultiPolygon") {
    return coordinates.some((poly) => pointInPolygon(lat, lng, poly));
  }
  return false;
}

/* Inline styles for map label overlays */
const LABEL_STYLES_ID = "nap-map-label-styles";
if (!document.querySelector(`#${LABEL_STYLES_ID}`)) {
  const style = document.createElement("style");
  style.id = LABEL_STYLES_ID;
  style.textContent = `
    .nap-city-label-tooltip {
      background: transparent !important;
      border: none !important;
      box-shadow: none !important;
      font-weight: 700;
      font-size: 13px;
      color: #1e3a8a;
      text-shadow: 0 0 4px #fff, 0 0 4px #fff, 0 0 4px #fff;
      white-space: nowrap;
    }
    .nap-city-label-tooltip::before { display: none !important; }
    .nap-brgy-label-icon span {
      font-size: 13px;
      font-weight: 700;
      color: #1d4ed8;
      text-shadow: 0 0 4px #fff, 0 0 4px #fff, 0 0 4px #fff;
      white-space: nowrap;
      pointer-events: none;
      letter-spacing: 0.03em;
    }
    @keyframes nap-pulse-ring {
      0% { transform: scale(1); opacity: 1; }
      70% { transform: scale(2.5); opacity: 0; }
      100% { transform: scale(2.5); opacity: 0; }
    }
    .nap-selected-pulse {
      width: 20px; height: 20px;
      border-radius: 50%;
      border: 3px solid #ef4444;
      background: transparent;
      animation: nap-pulse-ring 1.5s ease-out infinite;
      pointer-events: none;
    }
    @keyframes nap-search-pulse {
      0% { transform: scale(1); opacity: 1; }
      100% { transform: scale(1.6); opacity: 0; }
    }
  `;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// Address search pin — glowing yellow house marker (same as NI/Migration)
// ---------------------------------------------------------------------------
const ADDRESS_SEARCH_ICON = L.divIcon({
  className: "",
  html: `<div style="display:flex;flex-direction:column;align-items:center;pointer-events:auto;">
    <div style="
      display:flex;align-items:center;justify-content:center;
      width:60px;height:60px;border-radius:50%;
      background:#eab308;border:4px solid #fff;
      box-shadow:0 4px 14px rgba(234,179,8,0.6);
      animation:nap-search-pulse 2s ease-out infinite;
    "><svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg></div>
    <div style="width:4px;height:12px;background:#dc2626;"></div>
    <div style="width:14px;height:14px;background:#dc2626;border-radius:50%;border:2px solid #fff;box-shadow:0 2px 6px rgba(220,38,38,0.7);"></div>
  </div>`,
  iconSize: [60, 86],
  iconAnchor: [30, 86],
  popupAnchor: [0, -86],
});

// ---------------------------------------------------------------------------
// Head Office marker
// ---------------------------------------------------------------------------
const HEAD_OFFICE = [14.611065, 121.089339];
const HEAD_OFFICE_ICON = L.divIcon({
  className: "",
  html: `<div style="
    display:flex;align-items:center;justify-content:center;
    width:32px;height:32px;border-radius:50%;
    background:#1e40af;border:3px solid #fff;
    box-shadow:0 2px 8px rgba(0,0,0,0.3);
  "><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

// ---------------------------------------------------------------------------
// BrgyLabels helper (reused from NI map)
// ---------------------------------------------------------------------------
function BrgyLabels({ data }) {
  const map = useMap();
  useEffect(() => {
    if (!data || !map) return;
    const markers = [];
    (data.features || []).forEach((f) => {
      const name = f.properties?.brgy_name || "";
      if (!name) return;
      const centroid = L.geoJSON(f).getBounds().getCenter();
      const marker = L.marker(centroid, {
        icon: L.divIcon({
          className: "",
          html: `<span style="font-size:13px;font-weight:700;color:#1d4ed8;text-shadow:0 0 4px #fff,0 0 4px #fff,0 0 4px #fff;white-space:nowrap;pointer-events:none;letter-spacing:0.03em;">${name}</span>`,
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        }),
        interactive: false,
      });
      marker.addTo(map);
      markers.push(marker);
    });
    return () => markers.forEach((m) => map.removeLayer(m));
  }, [data, map]);
  return null;
}

// ---------------------------------------------------------------------------
// Grid-based clustering for performance with 13k+ points
// ---------------------------------------------------------------------------
function clusterPoints(points, zoom, mapBounds) {
  if (!mapBounds) return { clusters: [], singles: points };

  // At zoom >= 16, show individual points
  if (zoom >= 16) return { clusters: [], singles: points };

  // Grid cell size decreases as zoom increases (more detail at higher zoom)
  const gridSize = 80 / Math.pow(2, zoom - 10); // degrees-based grid

  const cells = {};
  const singles = [];

  for (const pt of points) {
    const cellX = Math.floor(pt.latitude / gridSize);
    const cellY = Math.floor(pt.longitude / gridSize);
    const key = `${cellX}_${cellY}`;
    if (!cells[key]) {
      cells[key] = { points: [], sumLat: 0, sumLng: 0 };
    }
    cells[key].points.push(pt);
    cells[key].sumLat += pt.latitude;
    cells[key].sumLng += pt.longitude;
  }

  const clusters = [];
  for (const key of Object.keys(cells)) {
    const cell = cells[key];
    if (cell.points.length === 1) {
      singles.push(cell.points[0]);
    } else {
      clusters.push({
        latitude: cell.sumLat / cell.points.length,
        longitude: cell.sumLng / cell.points.length,
        count: cell.points.length,
        points: cell.points,
      });
    }
  }

  return { clusters, singles };
}

function clusterRadius(count) {
  if (count >= 500) return 28;
  if (count >= 200) return 24;
  if (count >= 100) return 20;
  if (count >= 50) return 16;
  if (count >= 20) return 13;
  return 10;
}

// ---------------------------------------------------------------------------
// ZoomTracker - updates zoom state on map zoom changes
// ---------------------------------------------------------------------------
function ZoomTracker({ onZoomChange, onBoundsChange }) {
  const map = useMapEvents({
    zoomend() {
      onZoomChange(map.getZoom());
      onBoundsChange(map.getBounds());
    },
    moveend() {
      onBoundsChange(map.getBounds());
    },
  });
  useEffect(() => {
    onZoomChange(map.getZoom());
    onBoundsChange(map.getBounds());
  }, [map, onZoomChange, onBoundsChange]);
  return null;
}

// ---------------------------------------------------------------------------
// FlyToPoint - animated pan/zoom to a specific point
// ---------------------------------------------------------------------------
function FlyToPoint({ target, onDone }) {
  const map = useMap();
  useEffect(() => {
    if (target) {
      // Small delay ensures map is fully ready after view mode switch
      const flyTimer = setTimeout(() => {
        map.flyTo([target.latitude, target.longitude], 18, { duration: 1 });
      }, 100);
      if (onDone) {
        const doneTimer = setTimeout(onDone, 1200);
        return () => { clearTimeout(flyTimer); clearTimeout(doneTimer); };
      }
      return () => clearTimeout(flyTimer);
    }
  }, [target, map, onDone]);
  return null;
}

// ---------------------------------------------------------------------------
// MobilePanOffset - pans the map so the selected point sits in the upper third
// (avoids the bottom sheet covering it)
// ---------------------------------------------------------------------------
function MobilePanOffset({ selectedPoint, enabled, flyActive }) {
  const map = useMap();
  useEffect(() => {
    if (!enabled || !selectedPoint?.latitude || flyActive) return;
    // Pan so point is in upper 1/3 of the map viewport
    const timer = setTimeout(() => {
      const mapSize = map.getSize();
      const offsetY = mapSize.y * 0.25;
      const pointPixel = map.latLngToContainerPoint([selectedPoint.latitude, selectedPoint.longitude]);
      const targetPixel = L.point(pointPixel.x, pointPixel.y - offsetY);
      const targetLatLng = map.containerPointToLatLng(targetPixel);
      map.panTo(targetLatLng, { animate: true, duration: 0.3 });
    }, 150);
    return () => clearTimeout(timer);
  }, [selectedPoint, enabled, flyActive, map]);
  return null;
}

// ---------------------------------------------------------------------------
// Main NapLocatorMap component
// ---------------------------------------------------------------------------
export default function NapLocatorMap({ points, loading, error, onPointClick, onRetry, flyTo, onFlyDone, selectedPoint, onCloseDetail, onRecordUpdated, onViewInTable, mobileMode, onShowQr, searchPin, highlight }) {
  const { hasPerm } = useAuth();
  const { confirm } = useConfirm();
  const [zoom, setZoom] = useState(SERVICE_AREA_ZOOM);
  const [mapBounds, setMapBounds] = useState(null);
  const [brgyGeoJSON, setBrgyGeoJSON] = useState(null);
  const [cityGeoJSON, setCityGeoJSON] = useState(null);

  // Detail panel state
  const [record, setRecord] = useState(null);
  const [recordLoading, setRecordLoading] = useState(false);
  const [qrModal, setQrModal] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [generatingQr, setGeneratingQr] = useState(false);
  const [fetchingNapDetails, setFetchingNapDetails] = useState(false);
  const [napDetailsModal, setNapDetailsModal] = useState(null);

  const canEdit = hasPerm("nap_locator.edit");
  const canDelete = hasPerm("nap_locator.delete");
  const canQrGenerate = hasPerm("nap_locator.qr_generate");
  const canQrDelete = hasPerm("nap_locator.qr_delete");

  // Layer toggles
  const [showBasemap, setShowBasemap] = useState(true);
  const [showSatellite, setShowSatellite] = useState(false);
  const [showLandmarks, setShowLandmarks] = useState(false);
  const [showCityBoundaries, setShowCityBoundaries] = useState(true);
  const [showBrgyBoundaries, setShowBrgyBoundaries] = useState(false);
  const [showBrgyLabels, setShowBrgyLabels] = useState(true);
  const [showStreetLabels, setShowStreetLabels] = useState(true);

  // Right panel tab state
  const [rightPanelTab, setRightPanelTab] = useState("layers");

  // Geofence filter state
  const [filterMunicipality, setFilterMunicipality] = useState("");
  const [filterBarangay, setFilterBarangay] = useState([]);

  // Load boundary GeoJSON files
  useEffect(() => {
    fetch("/data/barangay_boundaries.geojson")
      .then((r) => r.json())
      .then(setBrgyGeoJSON)
      .catch(() => {});
    fetch("/data/city_boundaries.geojson")
      .then((r) => r.json())
      .then(setCityGeoJSON)
      .catch(() => {});
  }, []);

  // Derive municipality list from city GeoJSON
  const municipalityOptions = useMemo(() => {
    if (!cityGeoJSON?.features) return [];
    return cityGeoJSON.features
      .map((f) => f.properties.city_name)
      .filter(Boolean)
      .sort();
  }, [cityGeoJSON]);

  // Derive barangay list from brgy GeoJSON, filtered by selected municipality
  const barangayOptions = useMemo(() => {
    if (!brgyGeoJSON?.features) return [];
    let features = brgyGeoJSON.features;
    if (filterMunicipality) {
      features = features.filter((f) => f.properties.city_name === filterMunicipality);
    }
    return features
      .map((f) => f.properties.brgy_name)
      .filter(Boolean)
      .sort();
  }, [brgyGeoJSON, filterMunicipality]);

  // Get the active geofence polygon feature(s) for filtering
  const geofenceFeatures = useMemo(() => {
    if (filterBarangay.length > 0 && brgyGeoJSON?.features) {
      return brgyGeoJSON.features.filter(
        (f) => filterBarangay.includes(f.properties.brgy_name) &&
               (!filterMunicipality || f.properties.city_name === filterMunicipality)
      );
    }
    if (filterMunicipality && cityGeoJSON?.features) {
      const cityFeature = cityGeoJSON.features.find(
        (f) => f.properties.city_name === filterMunicipality
      );
      return cityFeature ? [cityFeature] : [];
    }
    return [];
  }, [filterMunicipality, filterBarangay, brgyGeoJSON, cityGeoJSON]);

  // Filter points by geofence polygon(s)
  const geoFilteredPoints = useMemo(() => {
    if (geofenceFeatures.length === 0) return points;
    return points.filter((pt) => geofenceFeatures.some((feat) => pointInFeature(pt.latitude, pt.longitude, feat)));
  }, [points, geofenceFeatures]);

  // Fetch full record when a point is selected
  useEffect(() => {
    if (!selectedPoint?.id) { setRecord(null); return; }
    setRecordLoading(true);
    setRightPanelTab("details");
    api.get("/nap-locator", { params: { search: selectedPoint.nap_code, page: 1, page_size: 1 } })
      .then((res) => {
        const found = res.data.find((r) => r.id === selectedPoint.id);
        setRecord(found || null);
      })
      .catch(() => setRecord(null))
      .finally(() => setRecordLoading(false));
  }, [selectedPoint]);

  const handleGenerateQr = async () => {
    if (!record) return;
    setGeneratingQr(true);
    try {
      const { data } = await api.post(`/nap-locator/${record.id}/qr/preview`);
      setQrModal({ record, mode: "preview", previewData: data });
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to generate QR preview.");
    } finally {
      setGeneratingQr(false);
    }
  };

  const handleNapDetails = async () => {
    if (!record) return;
    setFetchingNapDetails(true);
    try {
      const { data } = await api.post(`/nap-locator/${record.id}/nap-details`);
      if (data.found) {
        setNapDetailsModal(data);
        onRecordUpdated();
        // Refetch record
        if (selectedPoint) {
          api.get("/nap-locator", { params: { search: selectedPoint.nap_code, page: 1, page_size: 1 } })
            .then((res) => {
              const found = res.data.find((r) => r.id === selectedPoint.id);
              if (found) setRecord(found);
            })
            .catch(() => {});
        }
      } else {
        await confirm({
          title: "No NAP Details Found",
          message: data.detail || `No NAP details found for "${record.nap_code}" on the external server.`,
          confirmLabel: "OK",
          cancelLabel: "Close",
        });
        try {
          await api.post(`/nap-locator/${record.id}/no-stencil`);
          onRecordUpdated();
          if (selectedPoint) {
            api.get("/nap-locator", { params: { search: selectedPoint.nap_code, page: 1, page_size: 1 } })
              .then((res) => {
                const found = res.data.find((r) => r.id === selectedPoint.id);
                if (found) setRecord(found);
              })
              .catch(() => {});
          }
        } catch (e) {}
      }
    } catch (err) {
      await confirm({
        title: "NAP Details Error",
        message: err.response?.data?.detail || "Failed to fetch NAP details from external server.",
        confirmLabel: "OK",
        cancelLabel: "Close",
        variant: "danger",
      });
    } finally {
      setFetchingNapDetails(false);
    }
  };

  const handleDelete = async () => {
    if (!record) return;
    const ok = await confirm({
      title: "Delete NAP Record",
      message: `Are you sure you want to delete "${record.nap_code}"? This action cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/nap-locator/${record.id}`);
      onCloseDetail();
      onRecordUpdated();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete record.");
    }
  };

  const handleQrSaved = () => {
    setQrModal(null);
    onRecordUpdated();
    // Refetch record to update QR display in panel
    if (selectedPoint) {
      api.get("/nap-locator", { params: { search: selectedPoint.nap_code, page: 1, page_size: 1 } })
        .then((res) => {
          const found = res.data.find((r) => r.id === selectedPoint.id);
          if (found) setRecord(found);
        })
        .catch(() => {});
    }
  };

  const handleEditSaved = () => {
    setEditModal(false);
    onRecordUpdated();
    // Refetch record
    if (selectedPoint) {
      api.get("/nap-locator", { params: { search: selectedPoint.nap_code, page: 1, page_size: 1 } })
        .then((res) => {
          const found = res.data.find((r) => r.id === selectedPoint.id);
          if (found) setRecord(found);
        })
        .catch(() => {});
    }
  };

  const handleZoomChange = useCallback((z) => {
    setZoom(z);
    setShowLandmarks(z >= 17);
    setShowSatellite(z >= 20);
  }, []);
  const handleBoundsChange = useCallback((b) => setMapBounds(b), []);

  // Spiderfy state: when multiple points share the same location
  const [spiderfied, setSpiderfied] = useState(null); // { center, points, spread }

  // Filter points to visible bounds for rendering efficiency
  // On mobile, only show points when zoom >= 16
  const visiblePoints = useMemo(() => {
    if (mobileMode && zoom < 16) return [];
    if (!mapBounds || !geoFilteredPoints.length) return geoFilteredPoints;
    return geoFilteredPoints.filter((p) =>
      mapBounds.contains([p.latitude, p.longitude])
    );
  }, [geoFilteredPoints, mapBounds, mobileMode, zoom]);

  // Group points by location (rounded to ~11m precision) for overlap detection
  const locationGroups = useMemo(() => {
    const groups = {};
    for (const pt of geoFilteredPoints) {
      const key = `${pt.latitude.toFixed(5)}_${pt.longitude.toFixed(5)}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(pt);
    }
    return groups;
  }, [geoFilteredPoints]);

  // Visible location groups with 2+ NAP codes (for count badges) — only at high zoom
  const visibleMultiGroups = useMemo(() => {
    if (mobileMode && zoom < 16) return [];
    if (!mapBounds || zoom < 16) return [];
    const result = [];
    for (const key of Object.keys(locationGroups)) {
      const group = locationGroups[key];
      if (group.length < 2) continue;
      const pt = group[0];
      if (mapBounds.contains([pt.latitude, pt.longitude])) {
        result.push({ key, latitude: pt.latitude, longitude: pt.longitude, count: group.length });
      }
    }
    return result;
  }, [locationGroups, mapBounds, zoom, mobileMode]);

  // Handle marker click — spiderfy if multiple points at same location
  const handleMarkerClick = useCallback((pt) => {
    const key = `${pt.latitude.toFixed(5)}_${pt.longitude.toFixed(5)}`;
    const group = locationGroups[key];
    if (group && group.length > 1) {
      // Spiderfy: spread points in a circle
      const radius = 0.00015; // ~15m spread
      const spread = group.map((p, i) => {
        const angle = (2 * Math.PI * i) / group.length;
        return {
          ...p,
          spiderLat: pt.latitude + radius * Math.cos(angle),
          spiderLng: pt.longitude + radius * Math.sin(angle),
        };
      });
      setSpiderfied({ center: [pt.latitude, pt.longitude], points: spread });
    } else {
      setSpiderfied(null);
      onPointClick(pt);
    }
  }, [locationGroups, onPointClick]);

  // Close spiderfy when clicking elsewhere or changing zoom
  useEffect(() => {
    setSpiderfied(null);
  }, [zoom]);

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-100 rounded">
        <div className="text-center">
          <p className="text-gray-600 mb-3">Failed to load map data.</p>
          <button
            onClick={onRetry}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-100 rounded">
        <p className="text-gray-500">Loading map data...</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex min-h-0 gap-3">
      {/* Map */}
      <div className="flex-1 rounded-lg overflow-hidden shadow border border-gray-200 relative">
        {points.length === 0 && !mobileMode && (
          <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-white/80">
            <p className="text-gray-500 text-sm">No NAP records found on map.</p>
          </div>
        )}
        {mobileMode && zoom < 16 && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] bg-white/90 rounded-lg px-3 py-1.5 shadow text-xs text-gray-600 pointer-events-none">
            Zoom in more to see NAP points
          </div>
        )}
        <MapContainer
          center={SERVICE_AREA_CENTER}
          zoom={SERVICE_AREA_ZOOM}
          maxZoom={21}
          className="w-full h-full"
          style={{ minHeight: mobileMode ? "300px" : "400px" }}
          preferCanvas={true}
          zoomControl={true}
        >
          <ZoomTracker onZoomChange={handleZoomChange} onBoundsChange={handleBoundsChange} />
          {flyTo && <FlyToPoint target={flyTo} onDone={onFlyDone} />}
          {highlight && <NapPulseMarker position={highlight} />}

          {/* Base map layers */}
          {showBasemap && (
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={21}
            />
          )}
          {showSatellite && (
            <TileLayer
              attribution="&copy; Google"
              url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
              maxZoom={21}
            />
          )}
          {showStreetLabels && (
            <TileLayer
              key="road-labels"
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png"
              pane="overlayPane"
              maxZoom={21}
            />
          )}
          {showLandmarks && (
            <TileLayer
              key="landmarks"
              attribution="&copy; Google"
              url="https://mt1.google.com/vt/lyrs=h&x={x}&y={y}&z={z}"
              pane="overlayPane"
              maxZoom={21}
            />
          )}

          {/* City boundary layer (desktop only) */}
          {!mobileMode && showCityBoundaries && cityGeoJSON && (
            <GeoJSON
              key="nap-city-boundaries"
              data={cityGeoJSON}
              interactive={false}
              style={{
                color: "#1e40af",
                weight: 3,
                fillColor: "#3b82f6",
                fillOpacity: 0.04,
                dashArray: "6 4",
              }}
              onEachFeature={(feature, layer) => {
                const name = feature.properties.city_name || "";
                const shortName = name.replace(/^City of /i, "");
                layer.bindTooltip(shortName, {
                  permanent: true,
                  direction: "center",
                  className: "nap-city-label-tooltip",
                });
              }}
            />
          )}

          {/* Barangay boundary layer (desktop only) */}
          {!mobileMode && showBrgyBoundaries && brgyGeoJSON && (
            <GeoJSON
              key="nap-brgy-boundaries"
              data={brgyGeoJSON}
              interactive={false}
              style={{
                color: "#6b7280",
                weight: 1,
                fillColor: "#d1d5db",
                fillOpacity: 0.08,
              }}
            />
          )}

          {/* Barangay name labels (desktop only) */}
          {!mobileMode && showBrgyLabels && brgyGeoJSON && (
            <BrgyLabels data={brgyGeoJSON} />
          )}

          {/* Head Office marker */}
          <Marker position={HEAD_OFFICE} icon={HEAD_OFFICE_ICON} pane="markerPane">
            <Tooltip direction="top" offset={[0, -18]}>
              <div className="text-xs font-bold">BEST Inc. Head Office</div>
            </Tooltip>
          </Marker>

          {/* Address search pin */}
          {searchPin && (
            <Marker position={[searchPin.lat, searchPin.lng]} icon={ADDRESS_SEARCH_ICON} zIndexOffset={2000}>
              <Tooltip direction="top" offset={[0, -86]} permanent={false}>
                <div className="text-xs font-bold max-w-[200px] break-words">{searchPin.address || "Searched location"}</div>
              </Tooltip>
            </Marker>
          )}

          {/* Individual markers */}
          {visiblePoints.map((pt) => {
            let fillColor = "#eab308"; // yellow - Not Yet Updated
            if (pt.stencil === "N") {
              fillColor = "#3b82f6"; // blue - No Record
            } else if (pt.stencil === "Y" && pt.vacant === 0) {
              fillColor = "#ef4444"; // red - Full NAP
            } else if (pt.stencil === "Y" && pt.vacant >= 1) {
              fillColor = "#22c55e"; // green - Available
            }
            if (pt.manual_full_nap) fillColor = "#ef4444"; // manually flagged Full NAP overrides
            return (
            <CircleMarker
              key={`pt-${pt.id}`}
              center={[pt.latitude, pt.longitude]}
              radius={5}
              pathOptions={{
                fillColor,
                fillOpacity: 0.85,
                color: "#fff",
                weight: 1,
              }}
              eventHandlers={{
                click: () => handleMarkerClick(pt),
              }}
            >
              <Tooltip direction="top" offset={[0, -5]} opacity={0.9}>
                <div className="text-xs">
                  <div className="font-semibold">{pt.nap_code}</div>
                  <div className="text-gray-600">{pt.area || "—"}</div>
                  {pt.stencil === "Y" && (
                    <div className="text-gray-500 mt-0.5 border-t pt-0.5">
                      <span className="text-red-500">Used: {pt.used_port ?? "—"}</span>{" | "}
                      <span className="text-green-600">Vacant: {pt.vacant ?? "—"}</span>{" | "}
                      <span className="text-orange-600">ONU: {pt.onu_connect ?? "—"}</span>
                    </div>
                  )}
                </div>
              </Tooltip>
            </CircleMarker>
            );
          })}

          {/* Selected point highlight — pulsing ring + outer ring */}
          {selectedPoint && selectedPoint.latitude && selectedPoint.longitude && (
            <>
              <CircleMarker
                key="selected-ring"
                center={[selectedPoint.latitude, selectedPoint.longitude]}
                radius={14}
                pathOptions={{
                  fillColor: "transparent",
                  fillOpacity: 0,
                  color: "#ef4444",
                  weight: 3,
                  opacity: 0.9,
                }}
                interactive={false}
              />
              <Marker
                key="selected-pulse"
                position={[selectedPoint.latitude, selectedPoint.longitude]}
                interactive={false}
                icon={L.divIcon({
                  className: "",
                  html: `<div class="nap-selected-pulse"></div>`,
                  iconSize: [20, 20],
                  iconAnchor: [10, 10],
                })}
              />
            </>
          )}

          {/* Count badges for overlapping points (2+ NAP codes at same location) */}
          {visibleMultiGroups.map((g) => {
            // Hide only the badge for the currently spiderfied group
            if (spiderfied) {
              const spiderKey = `${spiderfied.center[0].toFixed(5)}_${spiderfied.center[1].toFixed(5)}`;
              if (g.key === spiderKey) return null;
            }
            return (
              <Marker
                key={`count-${g.key}`}
                position={[g.latitude, g.longitude]}
                interactive={false}
                icon={L.divIcon({
                  className: "",
                  html: `<div style="color:#1f2937;font-size:8px;font-weight:700;text-shadow:0 0 2px rgba(255,255,255,0.9);pointer-events:none;text-align:center;line-height:10px;">${g.count}</div>`,
                  iconSize: [10, 10],
                  iconAnchor: [5, 5],
                })}
              />
            );
          })}

          {/* Vacant port labels at max zoom (zoom >= 18) — single points only */}
          {zoom >= 18 && visiblePoints.map((pt) => {
            const key = `${pt.latitude.toFixed(5)}_${pt.longitude.toFixed(5)}`;
            const group = locationGroups[key];
            if (group && group.length > 1) return null;
            if (pt.vacant == null || pt.vacant === 0 || pt.stencil !== "Y" || pt.manual_full_nap) return null;
            return (
              <Marker
                key={`vacant-${pt.id}`}
                position={[pt.latitude, pt.longitude]}
                interactive={false}
                icon={L.divIcon({
                  className: "",
                  html: `<div style="position:absolute;left:8px;top:-8px;white-space:nowrap;background:#166534;color:#fff;font-size:10px;font-weight:700;padding:1px 4px;border-radius:8px;pointer-events:none;box-shadow:0 1px 3px rgba(0,0,0,0.3);">${pt.vacant}</div>`,
                  iconSize: [0, 0],
                  iconAnchor: [0, 0],
                })}
              />
            );
          })}

          {/* Vacant port total labels at max zoom (zoom >= 18) — multi-point groups */}
          {zoom >= 18 && visibleMultiGroups.map((g) => {
            const group = locationGroups[g.key];
            const totalVacant = group.reduce((sum, p) => sum + (p.stencil === "Y" && p.vacant != null ? p.vacant : 0), 0);
            if (totalVacant === 0) return null;
            return (
              <Marker
                key={`vacant-grp-${g.key}`}
                position={[g.latitude, g.longitude]}
                interactive={false}
                icon={L.divIcon({
                  className: "",
                  html: `<div style="position:absolute;left:8px;top:5px;white-space:nowrap;background:#166534;color:#fff;font-size:10px;font-weight:700;padding:1px 4px;border-radius:8px;pointer-events:none;box-shadow:0 1px 3px rgba(0,0,0,0.3);">${totalVacant}</div>`,
                  iconSize: [0, 0],
                  iconAnchor: [0, 0],
                })}
              />
            );
          })}

          {/* Spiderfied markers — spread out from overlapping point */}
          {spiderfied && spiderfied.points.map((pt) => {
            let fillColor = "#eab308";
            if (pt.stencil === "N") fillColor = "#3b82f6";
            else if (pt.stencil === "Y" && pt.vacant === 0) fillColor = "#ef4444";
            else if (pt.stencil === "Y" && pt.vacant >= 1) fillColor = "#22c55e";
            return (
              <CircleMarker
                key={`spider-${pt.id}`}
                center={[pt.spiderLat, pt.spiderLng]}
                radius={7}
                pathOptions={{
                  fillColor,
                  fillOpacity: 1,
                  color: "#1e40af",
                  weight: 2,
                }}
                eventHandlers={{
                  click: () => { setSpiderfied(null); onPointClick(pt); },
                }}
              >
                <Tooltip direction="top" offset={[0, -7]} opacity={0.95} sticky>
                  <div className="text-xs">
                    <div className="font-semibold">{pt.nap_code}</div>
                    <div className="text-gray-600">{pt.area || "—"}</div>
                    {pt.stencil === "Y" && (
                      <div className="text-gray-500 mt-0.5 border-t pt-0.5">
                        <span className="text-red-500">Used: {pt.used_port ?? "—"}</span>{" | "}
                        <span className="text-green-600">Vacant: {pt.vacant ?? "—"}</span>{" | "}
                        <span className="text-orange-600">ONU: {pt.onu_connect ?? "—"}</span>
                      </div>
                    )}
                  </div>
                </Tooltip>
              </CircleMarker>
            );
          })}

          {/* Vacant port labels on spiderfied points */}
          {spiderfied && zoom >= 18 && spiderfied.points.map((pt) => {
            if (pt.vacant == null || pt.vacant === 0 || pt.stencil !== "Y") return null;
            return (
              <Marker
                key={`spider-vacant-${pt.id}`}
                position={[pt.spiderLat, pt.spiderLng]}
                interactive={false}
                icon={L.divIcon({
                  className: "",
                  html: `<div style="position:absolute;left:10px;top:-8px;white-space:nowrap;background:#166534;color:#fff;font-size:10px;font-weight:700;padding:1px 4px;border-radius:8px;pointer-events:none;box-shadow:0 1px 3px rgba(0,0,0,0.3);">${pt.vacant}</div>`,
                  iconSize: [0, 0],
                  iconAnchor: [0, 0],
                })}
              />
            );
          })}
          })}
        </MapContainer>

        {/* Street Names checkbox — mobile only */}
        {mobileMode && (
          <div className="absolute top-[90px] left-[10px] z-[1000]">
            <label className="flex items-center gap-1.5 bg-white rounded-lg shadow-md border border-gray-200 px-2 py-1.5 cursor-pointer active:bg-gray-50">
              <input
                type="checkbox"
                checked={showStreetLabels}
                onChange={(e) => setShowStreetLabels(e.target.checked)}
                className="w-3.5 h-3.5 rounded"
              />
              <span className="text-[11px] font-medium text-gray-700">Street Names</span>
            </label>
          </div>
        )}

        {/* Legend */}
        <div className="absolute bottom-3 left-3 z-[1000] bg-white/95 rounded shadow-sm border p-2 text-xs">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-3 h-3 rounded-full bg-green-500 inline-block border border-white"></span>
            <span>Available</span>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <span className="w-3 h-3 rounded-full bg-red-500 inline-block border border-white"></span>
            <span>Full NAP</span>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <span className="w-3 h-3 rounded-full bg-blue-500 inline-block border border-white"></span>
            <span>No Record</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-yellow-500 inline-block border border-white"></span>
            <span>Not Yet Updated</span>
          </div>
          <div className="text-gray-400 mt-1 border-t pt-1">{geoFilteredPoints.length} points plotted</div>
        </div>
      </div>

      {/* Right Panel — Tabbed (desktop only) */}
      {!mobileMode && (
      <div className="w-64 lg:w-72 flex-shrink-0 flex flex-col min-h-0">
        {/* Tab Headers */}
        <MapPanelTabs
          accent="blue"
          active={rightPanelTab}
          onChange={setRightPanelTab}
          tabs={[
            { key: "filter", label: "Filter" },
            { key: "layers", label: "Layers" },
            { key: "details", label: "Details" },
          ]}
        />

        {/* Tab Content */}
        <div className="flex-1 min-h-0 overflow-y-auto bg-white rounded-b-lg shadow border border-t-0 border-gray-200 p-3">

          {/* Filter Tab */}
          {rightPanelTab === "filter" && (
            <div className="space-y-2">
              <div>
                <label className="block text-[10px] text-gray-500 mb-0.5">Municipality</label>
                <select
                  value={filterMunicipality}
                  onChange={(e) => { setFilterMunicipality(e.target.value); setFilterBarangay([]); }}
                  className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">All Municipalities</option>
                  {municipalityOptions.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-0.5">Barangay</label>
                <div className={`border border-gray-300 rounded max-h-60 overflow-y-auto px-1 py-1 ${!filterMunicipality ? 'opacity-50 pointer-events-none' : ''}`}>
                  {barangayOptions.length === 0 && (
                    <div className="text-[10px] text-gray-400 px-1 py-0.5">Select a municipality first</div>
                  )}
                  {barangayOptions.map((b) => (
                    <label key={b} className="flex items-center gap-1.5 px-1 py-0.5 text-xs text-gray-700 hover:bg-blue-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filterBarangay.includes(b)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilterBarangay((prev) => [...prev, b]);
                          } else {
                            setFilterBarangay((prev) => prev.filter((x) => x !== b));
                          }
                        }}
                        className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="truncate">{b}</span>
                    </label>
                  ))}
                </div>
              </div>
              {(filterMunicipality || filterBarangay.length > 0) && (
                <button
                  onClick={() => { setFilterMunicipality(""); setFilterBarangay([]); }}
                  className="text-[10px] text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear Filter
                </button>
              )}
            </div>
          )}

          {/* Layers Tab */}
          {rightPanelTab === "layers" && (
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                <input type="checkbox" checked={showBasemap} onChange={(e) => setShowBasemap(e.target.checked)} className="w-3.5 h-3.5 rounded" />
                Street Map
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                <input type="checkbox" checked={showSatellite} onChange={(e) => setShowSatellite(e.target.checked)} className="w-3.5 h-3.5 rounded" />
                Satellite
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                <input type="checkbox" checked={showCityBoundaries} onChange={(e) => setShowCityBoundaries(e.target.checked)} className="w-3.5 h-3.5 rounded" />
                City Boundaries
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                <input type="checkbox" checked={showBrgyBoundaries} onChange={(e) => setShowBrgyBoundaries(e.target.checked)} className="w-3.5 h-3.5 rounded" />
                Barangay Boundaries
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                <input type="checkbox" checked={showBrgyLabels} onChange={(e) => setShowBrgyLabels(e.target.checked)} className="w-3.5 h-3.5 rounded" />
                Barangay Names
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                <input type="checkbox" checked={showStreetLabels} onChange={(e) => setShowStreetLabels(e.target.checked)} className="w-3.5 h-3.5 rounded" />
                Street / Road Names
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                <input type="checkbox" checked={showLandmarks} onChange={(e) => setShowLandmarks(e.target.checked)} className="w-3.5 h-3.5 rounded" />
                Landmarks
              </label>
            </div>
          )}

          {/* NAP Details Tab */}
          {rightPanelTab === "details" && (
            <>
              {selectedPoint ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">NAP Details</div>
                    <button onClick={onCloseDetail} className="text-gray-400 hover:text-gray-600 text-sm leading-none">&times;</button>
                  </div>

                  {recordLoading ? (
                    <p className="text-xs text-gray-400 py-3 text-center">Loading...</p>
                  ) : record ? (
                    <div className="space-y-2 text-xs text-gray-700">
                      <div><span className="text-gray-400">NAP Code:</span> <span className="font-semibold text-gray-800">{record.nap_code}</span></div>
                      <div><span className="text-gray-400">No of Ports:</span> {record.no_of_ports ?? "—"}</div>
                      <div><span className="text-gray-400">Latitude:</span> {record.latitude ?? "—"}</div>
                      <div><span className="text-gray-400">Longitude:</span> {record.longitude ?? "—"}</div>
                      <div><span className="text-gray-400">Area:</span> {record.area || "—"}</div>
                      <div><span className="text-gray-400">Used Port:</span> {record.used_port ?? "—"}</div>
                      <div><span className="text-gray-400">Unused/Vacant Port:</span> {record.vacant ?? "—"}</div>
                      <div><span className="text-gray-400">ONU Connect:</span> {record.onu_connect ?? "—"}</div>
                      <div><span className="text-gray-400">With Record:</span> {record.stencil ?? "—"}</div>
                      <div><span className="text-gray-400">Updated By:</span> {record.updated_by_name || "—"}</div>

                      {/* QR Code */}
                      <div className="border-t pt-2 mt-2">
                        {record.qr_code_data ? (
                          <div className="flex flex-wrap items-start gap-1.5">
                            <img
                              src={`data:image/png;base64,${record.qr_code_data}`}
                              alt="QR"
                              className="w-24 h-24 border rounded cursor-pointer hover:opacity-80"
                              onClick={() => setQrModal({ record, mode: "view", previewData: null })}
                            />
                            <button
                              onClick={handleNapDetails}
                              disabled={fetchingNapDetails}
                              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                              {fetchingNapDetails ? "Fetching..." : "NAP Details"}
                            </button>
                          </div>
                        ) : canQrGenerate ? (
                          <div className="flex flex-wrap gap-1.5">
                            <button
                              onClick={handleGenerateQr}
                              disabled={generatingQr}
                              className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              {generatingQr ? "Generating..." : "Generate QR"}
                            </button>
                            <button
                              onClick={handleNapDetails}
                              disabled={fetchingNapDetails}
                              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                              {fetchingNapDetails ? "Fetching..." : "NAP Details"}
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400">No QR code</span>
                        )}
                  </div>

                  {/* Actions */}
                  <div className="border-t pt-2 mt-2 flex flex-wrap gap-1.5">
                    {canEdit && (
                      <button onClick={() => setEditModal(true)} className="px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600">Edit</button>
                    )}
                    {canDelete && (
                      <button onClick={handleDelete} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600">Delete</button>
                    )}
                    {onViewInTable && (
                      <button
                        onClick={() => {
                          const parts = record.nap_code.trim().split(/\s+/);
                          const lcp = parts.length >= 2 ? `${parts[0]} ${parts[1]}` : record.nap_code;
                          onViewInTable(lcp);
                        }}
                        className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        View on Table
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-400 py-3 text-center">Select a NAP point</p>
              )}
                </div>
              ) : (
                <p className="text-xs text-gray-400 py-3 text-center">Select a NAP point on the map</p>
              )}
            </>
          )}

        </div>
      </div>
      )}

      {/* Mobile NAP Details Popup — persistent bottom sheet */}
      {mobileMode && selectedPoint && (
        <div className="absolute bottom-0 left-0 right-0 z-[1500] pointer-events-none">
          <div
            className="pointer-events-auto w-full max-h-[55vh] bg-white rounded-t-2xl shadow-xl overflow-y-auto border-t"
          >
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">NAP Details</span>
              <button onClick={onCloseDetail} className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 active:bg-gray-200 text-lg leading-none">&times;</button>
            </div>
            <div className="px-4 py-3">
              {recordLoading ? (
                <p className="text-sm text-gray-400 py-6 text-center">Loading...</p>
              ) : record ? (
                <div className="space-y-2.5 text-sm text-gray-700">
                  <div><span className="text-gray-400 text-xs">NAP Code:</span> <span className="font-semibold text-gray-900">{record.nap_code}</span></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-gray-400 text-xs">Ports:</span> {record.no_of_ports ?? "—"}</div>
                    <div><span className="text-gray-400 text-xs">Used Port:</span> {record.used_port ?? "—"}</div>
                    <div><span className="text-gray-400 text-xs">Vacant:</span> {record.vacant ?? "—"}</div>
                    <div><span className="text-gray-400 text-xs">Record:</span> {record.stencil ?? "—"}</div>
                    <div><span className="text-gray-400 text-xs">ONU:</span> {record.onu_connect ?? "—"}</div>
                  </div>
                  <div><span className="text-gray-400 text-xs">Area:</span> {record.area || "—"}</div>
                  <div><span className="text-gray-400 text-xs">Lat/Lng:</span> {record.latitude ?? "—"}, {record.longitude ?? "—"}</div>
                  <div><span className="text-gray-400 text-xs">Updated By:</span> {record.updated_by_name || "—"}</div>

                  {/* Actions */}
                  <div className="border-t pt-3 mt-3 flex flex-wrap gap-2">
                    {record.qr_code_data && onShowQr && (
                      <button
                        onClick={() => onShowQr(record.qr_code_data, record.nap_code)}
                        className="px-3 py-1.5 text-xs bg-gray-800 text-white rounded-lg font-medium flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h7v7H3V3zm11 0h7v7h-7V3zm-11 11h7v7H3v-7zm14 3h.01M17 17h.01M21 17h.01M14 14h3v3h-3v-3zm3 3h3v3h-3v-3z" />
                        </svg>
                        QR Code
                      </button>
                    )}
                    <button
                      onClick={handleNapDetails}
                      disabled={fetchingNapDetails}
                      className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg font-medium disabled:opacity-50"
                    >
                      {fetchingNapDetails ? "Fetching..." : "NAP Details"}
                    </button>
                    {onViewInTable && (
                      <button
                        onClick={() => {
                          const parts = record.nap_code.trim().split(/\s+/);
                          const lcp = parts.length >= 2 ? `${parts[0]} ${parts[1]}` : record.nap_code;
                          onViewInTable(lcp);
                        }}
                        className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg font-medium"
                      >
                        View on Table
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400 py-6 text-center">No details available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* QR Modal */}
      {qrModal && (
        <NapQrModal
          record={qrModal.record}
          mode={qrModal.mode}
          previewData={qrModal.previewData}
          onClose={() => setQrModal(null)}
          onSaved={handleQrSaved}
          canDeleteQr={canQrDelete}
          canEdit={canEdit}
        />
      )}

      {/* Edit Modal */}
      {editModal && record && (
        <NapEditModal
          record={record}
          onClose={() => setEditModal(false)}
          onSaved={handleEditSaved}
        />
      )}

      {/* NAP Details Result Modal */}
      {napDetailsModal && (
        <NapDetailsResultModal
          data={napDetailsModal}
          onClose={() => setNapDetailsModal(null)}
          onChanged={onRecordUpdated}
        />
      )}

      {/* Loading overlay when fetching NAP details */}
      {fetchingNapDetails && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20">
          <div className="relative flex items-center justify-center w-20 h-20">
            <svg
              className="absolute inset-0 w-full h-full animate-spin"
              style={{ animationDuration: "1.5s" }}
              viewBox="0 0 100 100"
              fill="none"
            >
              <polygon
                points="50,2 93,25 93,75 50,98 7,75 7,25"
                stroke="#2563eb"
                strokeWidth="4"
                fill="none"
                strokeLinecap="round"
                strokeDasharray="12 6"
              />
            </svg>
            <img
              src="/best-icon.png"
              alt="Loading..."
              className="h-12 w-12"
            />
          </div>
        </div>
      )}
    </div>
  );
}
